// A month box that works in every browser. Safari on macOS has no <input type="month">: it shows a plain text box
// whose every keystroke made an invalid month, so the month could not be changed there (Oct 2026).
// The value is 'YYYY-MM', '' for none.
import React from 'react';
import { SxProps, Theme } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

export interface MonthFieldProps {
  label: string;
  /** 'YYYY-MM', or '' for none. */
  value: string;
  /** Called with a whole valid month, or '' when the box is cleared. */
  onChange: (month: string) => void;
  /** Earliest and latest month that can be picked, 'YYYY-MM'. */
  min?: string;
  max?: string;
  /** Show a button that empties the box. */
  clearable?: boolean;
  sx?: SxProps<Theme>;
}

/** '2026-10' -> 1 Oct 2026 (local time); null for anything else. */
export function monthToDate(month: string | null | undefined): Date | null {
  const m = /^(\d{4})-(\d{2})$/.exec(String(month || ''));
  if (!m || Number(m[2]) < 1 || Number(m[2]) > 12) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, 1);
}

/** A date -> 'YYYY-MM'; '' for none or an invalid date. */
export function dateToMonth(date: Date | null | undefined): string {
  if (!date || Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

// An empty box reads "Month Year" instead of the format's "MMMM YYYY".
const LOCALE_TEXT = { fieldMonthPlaceholder: () => 'Month', fieldYearPlaceholder: () => 'Year' };

const MonthField: React.FC<MonthFieldProps> = ({ label, value, onChange, min, max, clearable = false, sx }) => (
  <LocalizationProvider dateAdapter={AdapterDateFns} localeText={LOCALE_TEXT}>
    <DatePicker
      label={label}
      views={['year', 'month']}
      openTo="month"
      format="MMMM yyyy"
      value={monthToDate(value)}
      minDate={monthToDate(min) ?? undefined}
      maxDate={monthToDate(max) ?? undefined}
      onChange={(date, context) => {
        // Typing calls this on every keystroke; only a whole month inside min/max is passed on.
        if (date === null) {
          if (clearable) onChange('');
          return;
        }
        if (context.validationError) return;
        const month = dateToMonth(date);
        if (month) onChange(month);
      }}
      slotProps={{
        textField: { size: 'small', InputLabelProps: { shrink: true }, sx: { width: 200, ...(sx as object) } },
        field: { clearable },
        // On a phone the box opens a dialog and can't be typed in, so Clear goes there.
        actionBar: ({ wrapperVariant }) => ({
          actions: wrapperVariant === 'desktop' ? [] : clearable ? ['clear', 'cancel', 'accept'] : ['cancel', 'accept'],
        }),
      }}
    />
  </LocalizationProvider>
);

export default MonthField;
